import {
  DataGrid,
  Dialog,
  FilterToolbar,
  formatDate,
  StatusBadge,
  type FilterDef,
  type GridQuery,
  type Severity
} from '@ijoin/components';
import {
  AdminRecordKeeperExportQueueRowRes,
  GetAdminRecordKeeperExportQueuesParams,
  useGetAdminAdvisoryOrgRecordKeeperExportQueues,
  useGetAdminRecordKeeperExportQueues
} from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';

import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';
import { QueryBoundary } from './QueryBoundary';
import { SubmissionDetailsTabs } from './SubmissionDetailsTabs';
import { SubmissionLogReprocessDialog } from './SubmissionLogReprocessDialog';
import { useAdvisoryOrganizationOptions } from './useAdvisoryOrganizationOptions';
import { usePlanOptions } from './usePlanOptions';

type Option = { label: string; value: string };

const STATUS_SEVERITY: Record<string, Severity> = {
  'Export Completed': 'success',
  'Exported with Errors': 'error',
  'Export Started': 'info',
  'Pending Export': 'warning'
};

const JOB_STATUS_OPTIONS: Option[] = [
  { label: 'Exported with Errors', value: 'Exported with Errors' },
  { label: 'Export Completed', value: 'Export Completed' },
  { label: 'Export Started', value: 'Export Started' },
  { label: 'Export Pending', value: 'Pending Export' }
];

const SYSTEM_TASK_OPTIONS: Option[] = [
  { label: 'MAP Rebalance', value: 'MAP_Rebalance' },
  { label: 'Default Enrollment', value: 'Default_Enrollment' },
  { label: 'Unknown', value: 'Unknown' }
];

const formatDateTime = (value: unknown) =>
  value ? formatDate(value as string, { format: 'MM/DD/YYYY h:mm:ss A' }) : '';

type SubmissionLogProps =
  | { advisoryOrganizationId: string; mode: 'org' }
  | { advisoryOrganizationId?: undefined; mode: 'global' };

export const SubmissionLogTab: FC<SubmissionLogProps> = props => {
  const queryClient = useQueryClient();
  const [selected, setSelected] =
    useState<AdminRecordKeeperExportQueueRowRes | null>(null);
  const [reprocessTarget, setReprocessTarget] =
    useState<AdminRecordKeeperExportQueueRowRes | null>(null);
  const [query, setQuery] = useState<GridQuery>();

  const search = useSearch({ strict: false }) as {
    advisoryOrganizationId?: string;
    jobStatus?: string;
    planId?: string;
    queueDateTimeFrom?: string;
    queueDateTimeTo?: string;
  };

  const [draftOrgId, setDraftOrgId] = useState<string | undefined>();

  const appliedOrgId =
    props.mode === 'org'
      ? props.advisoryOrganizationId
      : (draftOrgId ??
        (query as GetAdminRecordKeeperExportQueuesParams | undefined)
          ?.advisoryOrganizationId);

  const orgs = useAdvisoryOrganizationOptions(props.mode === 'global');
  const plans = usePlanOptions(appliedOrgId);

  const onReprocessSuccess = () => {
    queryClient.invalidateQueries({
      predicate: q =>
        q.queryKey.some(
          k =>
            typeof k === 'string' && k.includes('record-keeper-export-queues')
        )
    });
  };

  const onCloseDetailsDialog = () => setSelected(null);
  const onCloseReprocessDialog = () => setReprocessTarget(null);

  const filters: FilterDef[] = [
    ...(props.mode === 'global'
      ? [
          {
            isLoading: orgs.isLoading,
            label: 'Advisory Organization',
            name: 'advisoryOrganizationId',
            options: orgs.data ?? [],
            type: 'select' as const
          }
        ]
      : []),
    {
      ...plans,
      label: 'Plan',
      name: 'planId',
      type: 'autocomplete'
    },
    { label: 'External Plan ID', name: 'externalPlanId', type: 'text' },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    {
      label: 'Status',
      name: 'jobStatus',
      options: JOB_STATUS_OPTIONS,
      type: 'select'
    },
    {
      label: 'System Task',
      name: 'systemTask',
      options: SYSTEM_TASK_OPTIONS,
      type: 'select'
    },
    {
      endName: 'endQueueDateTime',
      label: 'Queued',
      startName: 'startQueueDateTime',
      type: 'dateRange'
    },
    {
      endName: 'endScheduledReleaseDateTime',
      label: 'Scheduled Release',
      startName: 'startScheduledReleaseDateTime',
      type: 'dateRange'
    },
    {
      endName: 'endProcessedDateTime',
      label: 'Processed',
      startName: 'startProcessedDateTime',
      type: 'dateRange'
    }
  ];

  const orgSubmissions = useGetAdminAdvisoryOrgRecordKeeperExportQueues(
    props.advisoryOrganizationId ?? '',
    query as GetAdminRecordKeeperExportQueuesParams,
    {
      query: {
        enabled: props.mode === 'org' && !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const globalSubmissions = useGetAdminRecordKeeperExportQueues(
    query as GetAdminRecordKeeperExportQueuesParams,
    {
      query: {
        enabled: props.mode === 'global' && !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const submissions = props.mode === 'org' ? orgSubmissions : globalSubmissions;

  const orgColumn: GridColDef<AdminRecordKeeperExportQueueRowRes> = {
    field: 'advisoryOrganizationName',
    filterable: false,
    flex: 1,
    headerName: 'Advisory Organization',
    minWidth: 200,
    renderCell: params =>
      params.row.advisoryOrganizationId ? (
        <LinkButton
          params={{ advisoryOrganizationId: params.row.advisoryOrganizationId }}
          to='/organizations/$advisoryOrganizationId/submission-log'
          variant='text'>
          {params.row.advisoryOrganizationName ??
            params.row.advisoryOrganizationId}
        </LinkButton>
      ) : (
        (params.row.advisoryOrganizationName ?? '')
      ),
    sortable: false,
    type: 'string'
  };

  const columns: GridColDef<AdminRecordKeeperExportQueueRowRes>[] = [
    {
      align: 'center',
      disableColumnMenu: true,
      display: 'flex',
      field: 'details',
      filterable: false,
      headerName: '',
      renderCell: params => (
        <IconButton
          aria-label='Details'
          data-testid={`details-${params.row.id}`}
          onClick={() => setSelected(params.row)}>
          <InfoOutlinedIcon />
        </IconButton>
      ),
      sortable: false,
      width: 67
    },

    ...(props.mode === 'global' ? [orgColumn] : []),
    {
      field: 'planName',
      filterable: false,
      flex: 1,
      headerName: 'Plan',
      minWidth: 220,
      renderCell: params =>
        params.row.planId && params.row.advisoryOrganizationId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId'
            variant='text'>
            {params.row.planName ?? ''}
          </LinkButton>
        ) : (
          (params.row.planName ?? '')
        ),
      sortable: false,
      type: 'string'
    },
    {
      field: 'externalPlanId',
      filterable: false,
      flex: 0.75,
      headerName: 'External Plan ID',
      minWidth: 160,
      sortable: false,
      type: 'string'
    },
    {
      field: 'firstName',
      flex: 0.75,
      headerName: 'First Name',
      minWidth: 150,
      renderCell: params =>
        params.row.advisoryOrganizationId &&
        params.row.planEnrollmentId &&
        params.row.planId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planEnrollmentId: params.row.planEnrollmentId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
            variant='text'>
            {params.row.firstName ?? ''}
          </LinkButton>
        ) : (
          (params.row.firstName ?? '')
        ),
      type: 'string'
    },
    {
      field: 'lastName',
      flex: 0.75,
      headerName: 'Last Name',
      minWidth: 150,
      renderCell: params =>
        params.row.advisoryOrganizationId &&
        params.row.planEnrollmentId &&
        params.row.planId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planEnrollmentId: params.row.planEnrollmentId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
            variant='text'>
            {params.row.lastName ?? ''}
          </LinkButton>
        ) : (
          (params.row.lastName ?? '')
        ),
      type: 'string'
    },
    {
      field: 'jobStatus',
      flex: 0.75,
      headerName: 'Status',
      minWidth: 180,
      renderCell: params => (
        <StatusBadge
          severity={STATUS_SEVERITY[params.value as string] ?? 'info'}>
          {params.value as string}
        </StatusBadge>
      ),
      type: 'singleSelect',
      valueOptions: JOB_STATUS_OPTIONS
    },
    {
      field: 'systemTask',
      flex: 0.75,
      headerName: 'System Task',
      minWidth: 160,
      type: 'string'
    },
    {
      field: 'queueDateTime',
      flex: 0.75,
      headerName: 'Queued',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.queueDateTime ? new Date(row.queueDateTime) : null
    },
    {
      field: 'scheduledReleaseDateTime',
      flex: 0.75,
      headerName: 'Scheduled Release',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.scheduledReleaseDateTime
          ? new Date(row.scheduledReleaseDateTime)
          : null
    },
    {
      field: 'processedDateTime',
      flex: 0.75,
      headerName: 'Processed',
      minWidth: 170,
      type: 'date',
      valueFormatter: formatDateTime,
      valueGetter: (_value, row) =>
        row.processedDateTime ? new Date(row.processedDateTime) : null
    },
    {
      align: 'center',
      disableColumnMenu: true,
      display: 'flex',
      field: 'reprocessSubmission',
      filterable: false,
      headerName: '',
      renderCell: params => (
        <IconButton
          aria-label='Reprocess'
          data-testid={`reprocessSubmission-${params.row.id}`}
          onClick={() => setReprocessTarget(params.row)}>
          <ReplayIcon />
        </IconButton>
      ),
      sortable: false,
      width: 67
    }
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.id}
        initialFilters={{
          ...(props.mode === 'global' &&
            search.advisoryOrganizationId && {
              advisoryOrganizationId: search.advisoryOrganizationId
            }),
          ...(search.jobStatus && { jobStatus: search.jobStatus }),
          ...(search.planId && { planId: search.planId }),
          ...(search.queueDateTimeFrom && {
            startQueueDateTime: search.queueDateTimeFrom
          }),
          ...(search.queueDateTimeTo && {
            endQueueDateTime: search.queueDateTimeTo
          })
        }}
        initialSort={[{ field: 'queueDateTime', sort: 'desc' }]}
        loading={!query || submissions.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: [
            'details',
            ...(props.mode === 'global' ? ['advisoryOrganizationName'] : []),
            'planName'
          ],
          right: ['reprocessSubmission']
        }}
        rowCount={submissions.data?.pagination?.total ?? 0}
        rows={submissions.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'submissionLog'
          },
          toolbar: {
            onDraftChange: (values: Record<string, unknown>) =>
              setDraftOrgId(
                (values.advisoryOrganizationId as string) || undefined
              ),
            onRefresh: submissions.refetch,
            searchButtonTestId: 'submissionLogSearch',
            triggerTestId: 'submissionLogFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='submissionDetails'
        fullWidth
        isOpen={!!selected}
        maxWidth='xl'
        onClose={onCloseDetailsDialog}>
        <DialogTitle
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
          <Typography component='span' variant='h4'>
            {selected?.id}
          </Typography>
          <IconButton
            data-testid='closeSubmissionDetails'
            onClick={onCloseDetailsDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <QueryBoundary>
              <SubmissionDetailsTabs recordKeeperExportQueueId={selected.id} />
            </QueryBoundary>
          )}
        </DialogContent>
      </Dialog>

      <SubmissionLogReprocessDialog
        isOpen={!!reprocessTarget}
        onClose={onCloseReprocessDialog}
        onSuccess={onReprocessSuccess}
        recordKeeperExportQueueId={reprocessTarget?.id ?? null}
      />
    </Stack>
  );
};

SubmissionLogTab.displayName = 'SubmissionLogTab';
